import { Lightning, Registry } from '@lightningjs/sdk';
import { Theme } from '../../config/theme';
import { CmsConstants } from '../../Business/cms/CmsConstants';
import { CmsCardDummy } from './CmsCardDummy';
import { SubscriptionTypes } from '../../Business/detail/actions/epgRecordingActions/SubscriptionSettings.types';

export class CmsGrid extends Lightning.Component {
  static _template() {
    return {
      x: 0,
      y: 0,
      w: w => w,
      color: Theme.Colors.Transparent,
      rect: true,
      Title: {
        y: 0,
        h: Theme.Fonts.Ribbon.lineHeight,
        text: {
          ...Theme.Fonts.Ribbon.Title,
        },
        color: Theme.Colors.Ribbon.Title,
      },
      Cards: {
        color: 0xff800080,
      },
    };
  }

  _init() {
    this.focusIndex = -1;
    this.executeOnce = true;
    if (this.fadeOut === undefined) this.fadeOut = true;

    // the right edge of the last added card
    this.cardXCount = 0;
    this.cardYCount = 0;

    if (!this.contentOffset) this.contentOffset = 0;

    this.dummies = [];

    this.tag('Cards').h = this.data.sizeInfo.cardHeight;
    this.tag('Cards').x = this.contentOffset;
    this.tag('Cards').w = this.w - this.contentOffset;

    this.updateTitle();
    this.addItems().then(() => {
      if (!this.data.hasNext()) return;
      // add 2 dummies so centercard still centers last available item
      const items = this.tag('Cards');
      const lastItem = items.childList.last;
      this.dummies = [
        items.childList.a({
          type: CmsCardDummy,
          w: lastItem.data.sizeInfo.width,
          h: lastItem.data.sizeInfo.height,
          x: this.cardXCount,
          y: this.cardYCount,
          signals: {
            startLazyLoad: this._lazy.bind(this),
          },
        }),
      ];
    });
  }

  updateTitle() {
    const title = this.tag('Title');
    const items = this.tag('Cards');
    if (this.data.title) {
      const offset = this.contentOffset + CmsConstants.Ribbon.titleOffset;
      title.patch({
        visible: true,
        x: offset,
        w: w => w - offset,
        text: {
          text: this.data.title,
        },
      });
      items.y = Theme.Fonts.Ribbon.Title.lineHeight;
    } else {
      title.visible = false;
      items.y = 0;
    }
  }

  addItems() {
    const items = this.tag('Cards');

    // datasource get new cards with loadNext()
    return this.data.loadNext().then(newItems => {
      if (!newItems || newItems.length === 0) {
        // all items loaded
        return;
      }

      // add items
      const components = newItems.map(ds => {
        const [x, y] = this.nextCardXYPos(ds.sizeInfo.width, ds.sizeInfo.height);
        return {
          x,
          y,
          alpha: this.fadeOut ? CmsConstants.Animation.fadeOut : 1,
          ...ds.componentDescription(),
        };
      });
      items.childList.a(components);

      if (this.data.hasNext()) {
        // move dummies
        this.dummies.forEach(dummy => {
          items.childList.setAt(dummy, items.childList.length - 1);
          const [dummyX, dummyY] = this._calcXY(dummy.w, dummy.h, true);
          dummy.x = dummyX;
          dummy.y = dummyY;
          // hide and unhide the dummy for it to trigger active again...
          // grids generally do not load enough cards to push the dummy off-screen and make it trigger active again
          // this is still preferable over creating a new dummy each time
          dummy.visible = false;
          dummy.visible = true;
        });
      } else {
        // remove dummies
        this.dummies.forEach(dummy => {
          items.childList.remove(dummy);
        });
        this.dummies = [];
      }

      if (this.executeOnce) {
        this.focusIndex = 0;
        this.executeOnce = false;
        this._refocus();
        this._updateFadeOut();
      }
    });
  }

  nextCardXYPos(cardWidth, cardHeight) {
    const result = this._calcXY(cardWidth, cardHeight);
    this.h = this.cardYCount + cardHeight + Number(this.tag('Title').visible) * Theme.Fonts.Ribbon.Title.lineHeight;
    this.tag('Cards').h = this.cardYCount + cardHeight;
    return result;
  }

  _calcXY(cardWidth, cardHeight, dryRun = false) {
    let newX = this.cardXCount;
    let newY = this.cardYCount;
    if (newX === undefined || newY === undefined) return [0, 0];
    const margin = CmsConstants.Ribbon.margin;

    // check card being added positon for overflow
    if (this._cardOverflows(newX, newY, cardWidth, cardHeight)) {
      newY = newY + cardHeight + margin;
      newX = 0;
    }

    if (!dryRun) {
      // update right edge
      this.cardXCount = newX + cardWidth + margin;
      this.cardYCount = newY;
    }

    return [newX, newY];
  }

  _cardOverflows(posX, posY, width, _height) {
    const gridWidth = this.tag('Cards').finalW;
    return posX + width > gridWidth;
  }

  _refreshLayout() {
    this.cardXCount = 0;
    this.cardYCount = 0;
    const cards = this.tag('Cards');
    cards.childList.forEach(el => {
      const [x, y] = this.nextCardXYPos(el.w, el.h);
      el.x = x;
      el.y = y;
    });
  }

  _lazy() {
    // TODO: remove timeout. Change loadNext to activate after centerCard has happend
    Registry.setTimeout(() => {
      this.addItems();
    });
  }

  _getFocused() {
    if (this.focusIndex !== -1) {
      return this.tag('Cards').children[this.focusIndex];
    }
  }

  _focus() {
    // this._updateFadeOut(); // not required on focus, triggered by centerVertical
    if (this.focusIndex !== -1) {
      this.centerVertical();
    }
  }

  _unfocus() {
    this._updateFadeOut();
    this.fireAncestors('$hideMiniDetail');
  }

  _handleLeft() {
    const itemsPerRow = this.itemsPerRow();
    if (itemsPerRow && this.focusIndex % itemsPerRow === 0) {
      return false;
    }

    if (this.focusIndex > 0) {
      this.focusIndex = this.focusIndex - 1;
      this.centerVertical();
      return true;
    }
    return false;
  }

  _handleRight() {
    const itemsPerRow = this.itemsPerRow();
    if (itemsPerRow && this.focusIndex % itemsPerRow === itemsPerRow - 1) {
      return false;
    }

    const cards = this.tag('Cards');
    if (this.focusIndex < cards.children.length - 1) {
      this.focusIndex = this.focusIndex + 1;
      this.centerVertical();
      return true;
    }
    return false;
  }

  _handleDown() {
    const itemsPerRow = this.itemsPerRow();
    if (itemsPerRow > 0) {
      const cards = this.tag('Cards');
      if (this.focusIndex + itemsPerRow < cards.children.length) {
        this.focusIndex = this.focusIndex + itemsPerRow;
        this.centerVertical();
        return true;
      } else if (
        this.focusIndex < cards.children.length - 1 &&
        cards.children.length % itemsPerRow !== 0 &&
        cards.children.length > itemsPerRow &&
        Math.floor(this.focusIndex / itemsPerRow) !== Math.floor(cards.children.length / itemsPerRow)
      ) {
        this.focusIndex = cards.children.length - 1;
        this.centerVertical();
        return true;
      }
    }
    return false;
  }

  _handleUp() {
    const itemsPerRow = this.itemsPerRow();
    if (itemsPerRow > 0 && this.focusIndex - itemsPerRow >= 0) {
      this.focusIndex = this.focusIndex - itemsPerRow;
      this.centerVertical();
      return true;
    }
    return false;
  }

  centerVertical() {
    const card = this.tag('Cards').children[this.focusIndex];
    this._updateFadeOut();
    this.signal('centerVertical', this.finalY + this.tag('Cards').finalY + card.finalY + card.finalH * 0.5);
  }

  itemsPerRow() {
    if (this.tag('Cards').children.length === 0) {
      return 0;
    }
    return Math.floor(this.tag('Cards').finalW / this.tag('Cards').children[0].finalW);
  }

  $removeEpgElement(el, type = SubscriptionTypes.PROGRAM) {
    if (type === SubscriptionTypes.PROGRAM) {
      // remove the one
      this.tag('Cards').childList.remove(el);
      this._refreshLayout();
    } else if (type === SubscriptionTypes.SEASON) {
      // clear all items in grid
      this.tag('Cards').childList.clear();
    } else if (type === SubscriptionTypes.SERIES) {
      // signal to page to delete everything
      this.fireAncestors('$deleteSeries');
    }

    this.fireAncestors('$hideMiniDetail');
    this.focusIndex = Math.min(this.tag('Cards').childList.length - 1, this.focusIndex);
    this._refocus();
  }

  _updateFadeOut() {
    if (!this.fadeOut) return;
    const gridIsFocused = this.hasFocus();
    this.tag('Title').alpha = gridIsFocused ? 1 : CmsConstants.Animation.fadeOut;

    // fadeout of specific row in grid
    const itemsPerRow = this.itemsPerRow();
    const row = Math.floor(this.focusIndex / itemsPerRow);
    const firstItemInRow = row * itemsPerRow;
    const lastItemInRow = firstItemInRow + itemsPerRow - 1;
    this.tag('Cards').childList.forEach((child, idx) => {
      if (gridIsFocused && firstItemInRow <= idx && idx <= lastItemInRow) {
        child.setSmooth('alpha', 1);
      } else {
        child.setSmooth('alpha', CmsConstants.Animation.fadeOut);
      }
    });
  }
}
